import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Typography,
  message,
} from "antd";
import type { TablePaginationConfig } from "antd/es/table";
import type { FilterValue, SorterResult } from "antd/es/table/interface";
import dayjs, { Dayjs } from "dayjs";
import FileSaver from "file-saver";
import { getAll as getGoodsTags, type GoodsTag } from "../api/GoodsTags";
import {
  downloadFbsOrdersByArticleCsv,
  getFbsOrdersByArticle,
  getFbsOrdersByDay,
  getFbsOrdersByHour,
  getFbsOrdersCancellations,
  getFbsOrdersHeatmap,
  getFbsOrdersSummary,
  getFbsOpenSuppliesByWarehouse,
  postFbsOrdersBackfill,
  postFbsOrdersSync,
  type FbsOrdersArticleRow,
  type FbsOrdersByArticle,
  type FbsOrdersByDay,
  type FbsOrdersByHour,
  type FbsOrdersCancellations,
  type FbsOrdersHeatmap,
  type FbsOrdersSummary,
  type FbsOpenSuppliesByWarehouse,
} from "../api/FbsOrders";
import HourlyChart from "./HourlyChart";
import HeatmapGrid from "./HeatmapGrid";
import CancelRateChart from "./CancelRateChart";
import DailyBarChart from "./DailyBarChart";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const DOW_LABELS = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];

const pct = (v: number) => `${(v * 100).toFixed(1)}%`;
const money = (v: number) =>
  `${Math.round(v || 0).toLocaleString("ru-RU")} ₽`;

const SORTABLE = new Set([
  "article",
  "name",
  "marketplace",
  "units_ordered",
  "units_active",
  "units_cancelled",
  "cancel_rate",
  "revenue_ordered",
  "revenue_active",
  "revenue_cancelled",
]);

const sortOrderFor = (
  sort: string,
  order: "asc" | "desc",
  field: string
): "ascend" | "descend" | undefined =>
  sort === field ? (order === "asc" ? "ascend" : "descend") : undefined;

type OpenSupplyDayRow = {
  day: string;
  by_warehouse: Record<string, number>;
  total: number;
  __total?: boolean;
};

const FbsOrdersAnalytics = () => {
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(6, "day").startOf("day"),
    dayjs().endOf("day"),
  ]);
  const [marketplace, setMarketplace] = useState<string>("all");
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<string>("units_ordered");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [dayMetric, setDayMetric] = useState<
    "units_active" | "units_ordered" | "revenue_active"
  >("units_active");
  const [activeTab, setActiveTab] = useState("articles");
  const [tags, setTags] = useState<GoodsTag[]>([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState<FbsOrdersSummary | null>(null);
  const [byArticle, setByArticle] = useState<FbsOrdersByArticle | null>(null);
  const [byHour, setByHour] = useState<FbsOrdersByHour | null>(null);
  const [byDay, setByDay] = useState<FbsOrdersByDay | null>(null);
  const [heatmap, setHeatmap] = useState<FbsOrdersHeatmap | null>(null);
  const [cancels, setCancels] = useState<FbsOrdersCancellations | null>(null);
  const [openSupplies, setOpenSupplies] =
    useState<FbsOpenSuppliesByWarehouse | null>(null);
  const [csvLoading, setCsvLoading] = useState(false);

  useEffect(() => {
    getGoodsTags()
      .then(setTags)
      .catch(() => setTags([]));
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const params = useMemo(
    () => ({
      from: range[0].format("YYYY-MM-DD"),
      to: range[1].format("YYYY-MM-DD"),
      marketplace: marketplace === "all" ? undefined : marketplace,
      tag_ids: tagIds.length ? tagIds.join(",") : undefined,
      search: search || undefined,
    }),
    [range, marketplace, tagIds, search]
  );

  const filterHint = useMemo(() => {
    const parts: string[] = [];
    if (search) parts.push(`артикул «${search}»`);
    if (tagIds.length) parts.push(`теги: ${tagIds.length}`);
    if (marketplace !== "all") parts.push(marketplace);
    return parts.length ? parts.join(" · ") : "все артикулы";
  }, [search, tagIds, marketplace]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a, h, d, hm, c, os] = await Promise.all([
        getFbsOrdersSummary(params),
        getFbsOrdersByArticle({
          ...params,
          limit: "1000",
          sort,
          order,
        }),
        getFbsOrdersByHour(params),
        getFbsOrdersByDay(params),
        getFbsOrdersHeatmap(params),
        getFbsOrdersCancellations(params),
        getFbsOpenSuppliesByWarehouse({
          from: params.from,
          to: params.to,
        }),
      ]);
      setSummary(s);
      setByArticle(a);
      setByHour(h);
      setByDay(d);
      setHeatmap(hm);
      setCancels(c);
      setOpenSupplies(os);
    } catch (e: any) {
      message.error(e?.message || "Не удалось загрузить аналитику");
    } finally {
      setLoading(false);
    }
  }, [params, sort, order]);

  useEffect(() => {
    load();
  }, [load]);

  const onSync = async () => {
    try {
      message.loading({ content: "Синхронизация 48ч…", key: "sync" });
      const r: any = await postFbsOrdersSync();
      message.success({
        content: `Upserted ${r?.upserted ?? "?"} (wb=${r?.wbFetched ?? "?"}, ozon=${r?.ozonFetched ?? "?"})`,
        key: "sync",
      });
      await load();
    } catch (e: any) {
      message.error({ content: e?.message || "Sync failed", key: "sync" });
    }
  };

  const onBackfill7 = async () => {
    try {
      const to = dayjs().format("YYYY-MM-DD");
      const from = dayjs().subtract(6, "day").format("YYYY-MM-DD");
      message.loading({ content: "Backfill 7 дней…", key: "bf" });
      const r: any = await postFbsOrdersBackfill({ from, to });
      message.success({
        content: `Backfill: ${r?.days ?? "?"} дней, upserted=${r?.upserted ?? "?"}`,
        key: "bf",
      });
      await load();
    } catch (e: any) {
      message.error({ content: e?.message || "Backfill failed", key: "bf" });
    }
  };

  const onDownloadCsv = async () => {
    setCsvLoading(true);
    try {
      const response = await downloadFbsOrdersByArticleCsv({
        ...params,
        sort,
        order,
      });
      const blobPart = await response.blob();
      const blob = new Blob([blobPart], { type: "text/csv;charset=utf-8;" });
      FileSaver.saveAs(
        blob,
        `fbs-zakazy-artikuly-${params.from}_${params.to}.csv`
      );
    } catch (e: any) {
      message.error(e?.message || "Не удалось скачать CSV");
    } finally {
      setCsvLoading(false);
    }
  };

  const onArticleTableChange = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<FbsOrdersArticleRow>
      | SorterResult<FbsOrdersArticleRow>[]
  ) => {
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = String(s?.field || "");
    if (!SORTABLE.has(field)) return;
    setSort(field);
    setOrder(s.order === "ascend" ? "asc" : "desc");
  };

  const tagOptions = tags.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  return (
    <div style={{ padding: "8px 0 16px" }}>
      <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Row justify="space-between" align="middle" gutter={[8, 8]}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              FBS заказы
            </Title>
            <Text type="secondary">WB + Ozon · время МСК · цена продажи</Text>
          </Col>
          <Col>
            <Space wrap>
              <RangePicker
                value={range}
                onChange={(v) => {
                  if (v?.[0] && v?.[1]) setRange([v[0], v[1]]);
                }}
                allowClear={false}
              />
              <Select
                style={{ width: 140 }}
                value={marketplace}
                onChange={setMarketplace}
                options={[
                  { value: "all", label: "Все МП" },
                  { value: "wildberries", label: "WB" },
                  { value: "ozon", label: "Ozon" },
                ]}
              />
              <Button onClick={load} loading={loading}>
                Обновить
              </Button>
              <Button onClick={onSync}>Sync 48ч</Button>
              <Button onClick={onBackfill7}>Backfill 7д</Button>
            </Space>
          </Col>
        </Row>

        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Поиск по артикулу"
            style={{ width: 260 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onSearch={(v) => setSearch(v.trim())}
          />
          <Select
            mode="multiple"
            allowClear
            showSearch
            placeholder="Теги"
            style={{ minWidth: 240 }}
            value={tagIds}
            options={tagOptions}
            optionFilterProp="label"
            onChange={(ids: number[]) => setTagIds(ids)}
          />
        </Space>

        <Row gutter={[12, 12]}>
          <Col xs={12} md={4}>
            <Card loading={loading} size="small">
              <Statistic
                title="Заказано, ед."
                value={summary?.units_ordered ?? 0}
              />
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card loading={loading} size="small">
              <Statistic
                title="Активных, ед."
                value={summary?.units_active ?? 0}
              />
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card loading={loading} size="small">
              <Statistic
                title="Отменено, ед."
                value={summary?.units_cancelled ?? 0}
              />
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card loading={loading} size="small">
              <Statistic
                title="Выручка активных, ₽"
                value={money(summary?.revenue_active ?? 0)}
              />
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card loading={loading} size="small">
              <Statistic
                title="Выручка всего, ₽"
                value={money(summary?.revenue_ordered ?? 0)}
              />
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card loading={loading} size="small">
              <Statistic
                title="Доля отмен, %"
                value={pct(summary?.cancel_rate ?? 0)}
              />
            </Card>
          </Col>
        </Row>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: "articles",
              label: "По артикулам",
              children: (
                <Space
                  direction="vertical"
                  size="middle"
                  style={{ width: "100%" }}
                >
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Text type="secondary">{filterHint}</Text>
                    </Col>
                    <Col>
                      <Button
                        type="primary"
                        onClick={onDownloadCsv}
                        loading={csvLoading}
                      >
                        Скачать CSV
                      </Button>
                    </Col>
                  </Row>
                  <Table
                    loading={loading}
                    rowKey={(r) => `${r.marketplace}:${r.article}`}
                    size="small"
                    pagination={{ pageSize: 50 }}
                    dataSource={byArticle?.rows || []}
                    onChange={onArticleTableChange}
                    scroll={{ x: 1180 }}
                    columns={[
                      {
                        title: "Артикул",
                        dataIndex: "article",
                        width: 140,
                        sorter: true,
                        sortOrder: sortOrderFor(sort, order, "article"),
                      },
                      {
                        title: "Название",
                        dataIndex: "name",
                        ellipsis: true,
                        sorter: true,
                        sortOrder: sortOrderFor(sort, order, "name"),
                        render: (v: string | null) => v || "—",
                      },
                      {
                        title: "МП",
                        dataIndex: "marketplace",
                        width: 110,
                        sorter: true,
                        sortOrder: sortOrderFor(sort, order, "marketplace"),
                      },
                      {
                        title: "Заказано, ед.",
                        dataIndex: "units_ordered",
                        width: 120,
                        sorter: true,
                        sortOrder: sortOrderFor(sort, order, "units_ordered"),
                      },
                      {
                        title: "Активных, ед.",
                        dataIndex: "units_active",
                        width: 120,
                        sorter: true,
                        sortOrder: sortOrderFor(sort, order, "units_active"),
                      },
                      {
                        title: "Отменено, ед.",
                        dataIndex: "units_cancelled",
                        width: 120,
                        sorter: true,
                        sortOrder: sortOrderFor(
                          sort,
                          order,
                          "units_cancelled"
                        ),
                      },
                      {
                        title: "Выручка всего, ₽",
                        dataIndex: "revenue_ordered",
                        width: 140,
                        sorter: true,
                        sortOrder: sortOrderFor(
                          sort,
                          order,
                          "revenue_ordered"
                        ),
                        render: (v: number) => money(v),
                      },
                      {
                        title: "Выручка активных, ₽",
                        dataIndex: "revenue_active",
                        width: 150,
                        sorter: true,
                        sortOrder: sortOrderFor(sort, order, "revenue_active"),
                        render: (v: number) => money(v),
                      },
                      {
                        title: "Доля отмен, %",
                        dataIndex: "cancel_rate",
                        width: 120,
                        sorter: true,
                        sortOrder: sortOrderFor(sort, order, "cancel_rate"),
                        render: (v: number) => pct(v),
                      },
                    ]}
                  />
                </Space>
              ),
            },
            {
              key: "open_supplies",
              label: "Несобранные WB",
              children: (
                <Card
                  size="small"
                  loading={loading}
                  title="Несобранные заказы WB по дням"
                  extra={
                    <Text type="secondary">
                      сейчас в поставках (confirm) · итого{" "}
                      {openSupplies?.total_orders ?? 0}
                    </Text>
                  }
                >
                  <Text
                    type="secondary"
                    style={{ display: "block", marginBottom: 12 }}
                  >
                    По дате создания заказа за выбранный период: сколько заказов
                    с этого дня сейчас ещё на сборке. Склад — маркер поставки
                    (ИГ/МШ). Единицы — заказы, шт.
                  </Text>
                  <Table<OpenSupplyDayRow>
                    size="small"
                    pagination={false}
                    scroll={{ x: true }}
                    rowKey={(r) => r.day}
                    dataSource={
                      [
                        ...(openSupplies?.day_rows || []),
                        ...(openSupplies
                          ? [
                              {
                                day: "Итого",
                                by_warehouse: openSupplies.totals_by_warehouse,
                                total: openSupplies.total_orders,
                                __total: true,
                              },
                            ]
                          : []),
                      ] as OpenSupplyDayRow[]
                    }
                    locale={{ emptyText: "Нет несобранных заказов за период" }}
                    columns={[
                      {
                        title: "День",
                        dataIndex: "day",
                        key: "day",
                        fixed: "left" as const,
                        width: 120,
                        render: (day: string, row) =>
                          row.__total ? <Text strong>{day}</Text> : day,
                      },
                      ...(openSupplies?.warehouses || []).map((wh) => ({
                        title: `${wh.warehouse_name}, шт.`,
                        key: wh.key,
                        align: "right" as const,
                        width: 140,
                        render: (_: unknown, row: OpenSupplyDayRow) => {
                          const n = row.by_warehouse?.[wh.key] || 0;
                          return row.__total ? <Text strong>{n}</Text> : n;
                        },
                      })),
                      {
                        title: "Итого, шт.",
                        dataIndex: "total",
                        key: "total",
                        align: "right" as const,
                        fixed: "right" as const,
                        width: 110,
                        render: (v: number, row) =>
                          row.__total ? <Text strong>{v}</Text> : v,
                      },
                    ]}
                  />
                </Card>
              ),
            },
            {
              key: "by_day",
              label: "По дням",
              children: (
                <Card
                  loading={loading}
                  title="Продажи по дням (МСК)"
                  extra={
                    <Space>
                      <Text type="secondary">{filterHint}</Text>
                      <Select
                        style={{ width: 180 }}
                        value={dayMetric}
                        onChange={setDayMetric}
                        options={[
                          { value: "units_active", label: "Активных, ед." },
                          { value: "units_ordered", label: "Заказано, ед." },
                          { value: "revenue_active", label: "Выручка, ₽" },
                        ]}
                      />
                    </Space>
                  }
                >
                  <Text
                    type="secondary"
                    style={{ display: "block", marginBottom: 8 }}
                  >
                    Столбцы = выбранный фильтр (артикул / теги / все). Подписи
                    на столбцах — значение за день.
                  </Text>
                  <DailyBarChart
                    days={byDay?.days || []}
                    metric={dayMetric}
                  />
                </Card>
              ),
            },
            {
              key: "time",
              label: "По времени",
              children: (
                <Space
                  direction="vertical"
                  size="large"
                  style={{ width: "100%" }}
                >
                  <Card title="По часам (МСК)" loading={loading}>
                    <HourlyChart hours={byHour?.hours || []} />
                  </Card>
                  <Card
                    title="Heatmap: день недели × час (МСК)"
                    loading={loading}
                    extra={
                      <Text type="secondary">{DOW_LABELS.join(" · ")}</Text>
                    }
                  >
                    <HeatmapGrid cells={heatmap?.cells || []} />
                  </Card>
                </Space>
              ),
            },
            {
              key: "cancels",
              label: "Отмены",
              children: (
                <Space
                  direction="vertical"
                  size="large"
                  style={{ width: "100%" }}
                >
                  <Row gutter={16}>
                    <Col xs={24} md={14}>
                      <Card title="Доля отмен по дням, %" loading={loading}>
                        <CancelRateChart days={cancels?.by_day || []} />
                      </Card>
                    </Col>
                    <Col xs={24} md={10}>
                      <Card title="По источнику" loading={loading}>
                        <Table
                          size="small"
                          pagination={false}
                          rowKey="cancel_source"
                          dataSource={cancels?.by_source || []}
                          columns={[
                            {
                              title: "Источник",
                              dataIndex: "cancel_source",
                              sorter: (a, b) =>
                                a.cancel_source.localeCompare(b.cancel_source),
                            },
                            {
                              title: "Отменено, ед.",
                              dataIndex: "units_cancelled",
                              sorter: (a, b) =>
                                a.units_cancelled - b.units_cancelled,
                            },
                          ]}
                        />
                      </Card>
                    </Col>
                  </Row>
                  <Card title="Топ артикулов по отменам" loading={loading}>
                    <Table
                      size="small"
                      rowKey={(r) => `${r.marketplace}:${r.article}`}
                      pagination={{ pageSize: 30 }}
                      dataSource={cancels?.top_articles || []}
                      columns={[
                        {
                          title: "Артикул",
                          dataIndex: "article",
                          sorter: (a, b) => a.article.localeCompare(b.article),
                        },
                        {
                          title: "МП",
                          dataIndex: "marketplace",
                          width: 120,
                          sorter: (a, b) =>
                            a.marketplace.localeCompare(b.marketplace),
                        },
                        {
                          title: "Отменено, ед.",
                          dataIndex: "units_cancelled",
                          width: 120,
                          defaultSortOrder: "descend",
                          sorter: (a, b) =>
                            a.units_cancelled - b.units_cancelled,
                        },
                        {
                          title: "Заказано, ед.",
                          dataIndex: "units_ordered",
                          width: 120,
                          sorter: (a, b) => a.units_ordered - b.units_ordered,
                        },
                        {
                          title: "Доля отмен, %",
                          dataIndex: "cancel_rate",
                          width: 120,
                          sorter: (a, b) => a.cancel_rate - b.cancel_rate,
                          render: (v: number) => pct(v),
                        },
                      ]}
                    />
                  </Card>
                </Space>
              ),
            },
          ]}
        />
      </Space>
    </div>
  );
};

export default FbsOrdersAnalytics;
